import { axiosToken } from "@/utils/http"
import { defineStore, acceptHMRUpdate } from "pinia"
import dayjs from "@/utils/dayjs"

export interface ChangelogEntry {
	id: number
	version: string
	title: string
	body: string
	published_at: string | null
	created_at: string
	read: boolean
}

export const useChangelogStore = defineStore("changelog", {
	state: () => ({
		entries: [] as ChangelogEntry[],
		unread: [] as ChangelogEntry[],
		showBanner: false,
		loaded: false,
	}),
	actions: {
		async fetchEntries() {
			const response = await axiosToken("get", "changelog/entries/")
			this.entries = response.data
			this.entries.sort(
				(a, b) =>
					new Date(b.published_at || b.created_at).getTime() -
					new Date(a.published_at || a.created_at).getTime()
			)
			this.loaded = true
		},
		async fetchUnread() {
			const response = await axiosToken("get", "changelog/unread/")
			this.unread = response.data
			this.unread.sort(
				(a, b) =>
					new Date(b.published_at || b.created_at).getTime() -
					new Date(a.published_at || a.created_at).getTime()
			)
			this.showBanner = this.unread.length > 0
		},
		async markRead(id: number | string) {
			await axiosToken("post", `changelog/read/${id}/`)
			this._markLocalRead(Number(id))
		},
		async markAllRead() {
			await axiosToken("post", "changelog/read_all/")
			this.entries.forEach(e => {
				e.read = true
			})
			this.unread = []
			this.showBanner = false
		},
		dismissBanner() {
			this.showBanner = false
		},
		_markLocalRead(id: number) {
			const entry = this.entries.find(e => e.id === id)
			if (entry) entry.read = true
			this.unread = this.unread.filter(e => e.id !== id)
			if (this.unread.length === 0) this.showBanner = false
		},
	},
	getters: {
		latestUnread: (state): ChangelogEntry | null => {
			if (state.unread.length === 0) return null
			return state.unread[0]
		},
		hasUnread: (state): boolean => state.unread.length > 0,
		unreadCount: (state): number => state.unread.length,
		formatDate: () => (date: string | null) => {
			if (!date) return ""
			return dayjs(new Date(date)).format("DD/MM/YYYY")
		},
	},
})

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(useChangelogStore, import.meta.hot))
}
